import React from "react";
import { Link } from "react-router-dom";

import { Language, runShortcut } from "@utils";

import aliases from "@pages/learn/assets/aliases.png";
import TrueNegatives from "@pages/learn/assets/comments_and_strings.png";
import helloworld from "@pages/learn/assets/helloworld.png";
import TruePositive from "@pages/learn/assets/linebreak.png";
import train from "@pages/learn/assets/train.png";

interface Lesson {
  title: string;
  content: React.ReactNode;
  pattern: string;
  savedwork?: string;
  answers: string[];
  target: string;
  followupTitle?: string;
  followup?: React.ReactNode;
  language?: Language;
}

export const BASIC_LESSONS: Lesson[] = [
  {
    title: "Welcome to the Semgrep Tutorial!",
    content: (
      <div>
        <p>
          {" "}
          In this tutorial you will learn how to write Semgrep patterns to
          search your code.
        </p>
        <p>
          The simplest Semgrep pattern is one that looks exactly like your code.
        </p>
        <p>In fact, the </p>
        <img src={helloworld} width="180px" alt="print('Hello, World!')"></img>
        <p>in the top panel is already a Semgrep pattern!</p>
        <p>
          <em>
            Click the blue <strong>Run</strong> button or use{" "}
            <strong>{runShortcut}</strong> to get started.
          </em>
        </p>
      </div>
    ),
    pattern: `print('Hello, World!')`,
    answers: [`print('Hello, World!')`],
    target: `print('Hello, World!')\n\nprint('Hello,\\\n World!')\n\n#print('Hello, World!')\n\nx = "print('Hello, World!')"`,
    followupTitle: "Great!",
    followup: (
      <>
        <p>
          Notice how Semgrep finds the statement that is broken across two
          lines, because it is semantically the same,
        </p>
        <img
          src={TruePositive}
          width="150px"
          alt="lines 3 and 4 of the test code"
        ></img>
        <p>but does not match on comments or strings.</p>
        <img
          src={TrueNegatives}
          width="250px"
          alt="bottom two lines of code, which Semgrep did not find"
        ></img>
      </>
    ),
  },
  {
    title: "The ellipsis operator (...)",
    content: (
      <div>
        <p>
          The <strong>ellipsis operator</strong> <code>...</code> in Semgrep
          allows you to "skip over" things you don't care about like{" "}
          <code>.*</code> does in regular expressions, by abstracting away a
          sequence of zero or more expressions.
        </p>
        <p>
          <em>
            Change your pattern by replacing the <code>1</code> inside{" "}
            <code>get_user(1)</code> with an ellipsis. Then click{" "}
            <strong>Run</strong>.
          </em>
        </p>
      </div>
    ),
    pattern: `logging.info(get_user(1) + " logged in")`,
    answers: [`logging.info(get_user(...) + " logged in")`],
    target: `import logging as lg

def get_user(uid):
    d = {1: "harry", 2: "ron", 3: "hermione"}
    return d[uid]

# Match both of these using an ellipsis.
logging.info(get_user(1)
    + " logged in")
lg.info(get_user(2)
    + " logged in")`,
    followupTitle: "Perfect!",
    followup: (
      <>
        <p>Notice how</p>
        <img src={aliases} width="180px" alt="import logging as lg"></img>
        <p>
          makes Semgrep recognize <code>lg.info(...)</code> as a call to{" "}
          <code>logging.info(...)</code>.
        </p>
        <p>
          <em>However,</em>
        </p>
        <p>
          if you changed your Semgrep pattern to <code>lg.info(...)</code>{" "}
          Semgrep would no longer find this line of code, since it is just an
          alias.
        </p>
      </>
    ),
  },
  {
    title: `The ellipsis operator in strings "..."`,
    content: (
      <div>
        <p>
          You can also use the ellipsis operator inside of quotation marks "..."
          to tell Semgrep to match <strong>any constant string</strong>.
        </p>
        <p>
          <em>
            Try matching the first two uses of <code>logging.info</code> by
            using the ellipsis operator to match all strings. Click{" "}
            <strong>Run</strong> to see the matches.
          </em>
        </p>
      </div>
    ),
    pattern: `logging.info(get_user(...) + " flew in")`,
    answers: [
      `logging.info(get_user(...) + "...")`,
      `logging.info(get_user(...) + '...')`,
    ],
    target: `import logging

def get_user(uid):
    d = {1: "harry", 2: "ron", 3: "hermione"}
    return d[uid]

# Try matching these two cases by editing the pattern to match any string.
logging.info(get_user(1)
    + " flew in")
logging.info(get_user(2)
    + " apparated in")
#train = " arrived by train"
logging.info(get_user(3)
    + train)`,
    followupTitle: "Exactly!",
    followup: (
      <>
        <p>As an extension, if you uncomment the line</p>
        <img src={train} height="40px" alt="train = ' on a train'"></img>
        <p>
          and re-run the pattern, you will see that Semgrep propagates string
          constants, and now picks up that <code>train</code> is a string as
          well!
        </p>
      </>
    ),
  },
  {
    title: "The ellipsis operator: between code",
    content: (
      <div>
        <p>
          The ellipsis operator can ignore arbitrary sequences of expressions or
          statements, as well. Semgrep will match if the "ends" of the pattern
          match, ignoring what is in between them.
        </p>
        <p>
          <em>
            Write a pattern to match code blocks that start with{" "}
            <code>print("you")</code> and end with <code>print("world")</code>.
            Try adding more statements in the middle of the code, too!
          </em>
        </p>
      </div>
    ),
    pattern: `print("hello")\n...\nprint("world")`,
    answers: [`print("you")\n...\nprint("world")`],
    target: `print("first line")
print("hello")
print("you")
print("wonderful")
print("world")
print(".")`,
    followupTitle: "Awesome! 🎉",
    followup: (
      <>
        <p>Fun Fact: ellipses can be unbounded as well!</p>
        <p>
          Experiment with the pattern you just wrote to see what happens if you
          delete either the <code>print("you")</code> or the{" "}
          <code>print("world")</code> (or both!)
        </p>
        <span style={{ fontSize: "2em" }} role="img" aria-label=":thinking:">
          🤔
        </span>
        <p></p>
        <p>
          Up next: just one more use of ellipses, and then you will be a
          SemgrExpert (oof, bad pun!)
        </p>
      </>
    ),
  },
  {
    title: "The ellipsis operator: ordered lists",
    content: (
      <div>
        <p>
          {" "}
          When matching ordered lists of expressions, Semgrep will match only if
          the position of an expression is the same in the pattern and the code.
          So, the pattern <code>open("r")</code> will not match{" "}
          <code>open(filename, "r")</code>.
        </p>
        <p>
          Ellipses can be used to match the first or last expression in a list,
          like <code>open("r",&nbsp;...)</code> or{" "}
          <code>open(...,&nbsp;"r")</code>, respectively.
        </p>
        <p>
          To look for an expression at any location, use ellipses on both sides!
        </p>
        <p>
          <em>
            Write a pattern to match calls to <code>open</code> that contain an{" "}
            <code>"r"</code> anywhere in the argument list.
          </em>
        </p>
      </div>
    ),
    pattern: `open("file1", 'r')`,
    answers: [`open(..., 'r', ...)`, `open(..., "r", ...)`],
    target: `# match these
f = open("file1", 'r')
f = open("file2", 'r', -1)
f = open(filename, 'r')

# but do not match these
f = open("file1", 'w')
f = open("file2", 'w')`,
    followupTitle: "You're killin it! 🎉",
    followup: (
      <>
        <p>
          You've learned many of the uses of ellipses in Semgrep and are ready
          to move on to Semgrep's more advanced features.
        </p>
        <p>
          How would you use Semgrep to only match method calls from a specific
          library?
        </p>
        <p>
          Or ensure that a variable that is used in a dangerous function is
          first passed through a sanitizing function?
        </p>
        <p>
          Keep going to find out!
          <span style={{ fontSize: "2em" }} role="img" aria-label=":fire:">
            🔥
          </span>
        </p>
      </>
    ),
  },
  {
    title: "Metavariables",
    content: (
      <div>
        <p>
          <strong>Metavariables</strong> are an abstraction that lets you match{" "}
          <strong>something</strong> even when you don't know exactly what it is
          you want to match. Think of metavariables like capture groups in
          regular expressions.
        </p>
        <p>
          All metavariables must start with a dollar sign and can only contain
          uppercase characters, digits, and underscores.
        </p>
        <p>
          The pattern here uses a metavariable, <code>$FUNC</code>, to match any
          function name. As written, this pattern will match every function
          definition.
        </p>
        <p>
          <em>
            Try to match only function definitions that end with calls to{" "}
            <code>requests.get()</code> or <code>requests.post()</code>. You can
            use a metavariable named <code>$METHOD</code> to capture any method
            of the <code>requests</code> object.
          </em>
        </p>
      </div>
    ),
    pattern: `def $FUNC(...):
    ...`,
    answers: [
      `def $FUNC(...):
    ...
    requests.$METHOD(...)`,
      `def $FUNC(...):
    ...
    requests.$METHOD`,
    ],
    target: `def get_things(url):
  requests.get(url)

def post_things(url):
  requests.post(url)

# Try not to match here...
def print_things(thing):
  print(thing)

# Nor here.
def print_two_things(a, b):
  print(a)
  print(b)

def print_and_post_things(url):
  print("thing")
  requests.post(url)`,
    followupTitle: "Sweet!",
    followup: (
      <>
        <p>
          How does Semgrep know what to interpret as a metavariable and what is
          just a normal name, like <code>requests</code>?
        </p>
        <p>
          Remember: metavariables start with a dollar sign and can only contain
          uppercase characters, digits, and underscores.
        </p>
        <p>
          <span role="img" aria-label=":good:">
            🟢
          </span>{" "}
          <code>$X</code>
        </p>
        <p>
          <span role="img" aria-label=":good:">
            🟢
          </span>{" "}
          <code>$WIDGET</code>
        </p>
        <p>
          <span role="img" aria-label=":good:">
            🟢
          </span>{" "}
          <code>$USERS_2</code>
        </p>
        <p>
          <span role="img" aria-label=":bad:">
            🔴
          </span>{" "}
          <code>$x</code>
        </p>
        <p>
          <span role="img" aria-label=":bad:">
            🔴
          </span>{" "}
          <code>$some+value</code>
        </p>
      </>
    ),
  },
  {
    title: "A Common Pitfall",
    content: (
      <div>
        <p>
          Beware: Semgrep can’t match partial statements. Semgrep patterns must
          match an entire, parsable statement in the target language. If you
          click <strong>Run</strong>, Semgrep will fail to parse the following
          pattern
        </p>
        <p>
          <code>def get_user():</code>
        </p>
        <p>because it is missing a function body.</p>
        <p>
          <em>
            Try fixing this pattern by making it match any definition of{" "}
            <code>get_user</code> with any function body
          </em>
        </p>
      </div>
    ),
    pattern: `def get_user():`,
    answers: [
      `def get_user():
  ...`,
    ],
    target: `# Match this by fixing the pattern.
def get_user():
  print("do stuff")
  print("more stuff")`,
    followup: (
      <>
        <p>
          Pro tip: If you are ever having trouble getting Semgrep to parse your
          Semgrep pattern:
        </p>
        <p>
          <span role="img" aria-label=":one:">
            1️⃣
          </span>{" "}
          Copy and paste the code you want to match into your Semgrep pattern as
          a starting place
        </p>
        <p>
          <span role="img" aria-label=":two:">
            2️⃣
          </span>{" "}
          Generalize the pattern by replacing unimportant lines with ellipses,
          and adding ellipses where extra code could be placed
        </p>
        <p>
          <span role="img" aria-label=":three:">
            3️⃣
          </span>{" "}
          Replace the key variables with metavariables
        </p>
      </>
    ),
  },
  {
    title: "Matching using metavariables",
    content: (
      <div>
        <p>
          Metavariables can also be used like normal variables in a program.
        </p>
        <p>
          Say you want to search for places where a file was opened in{" "}
          <strong>read mode</strong>, but then written to. In Python, this will
          cause a runtime error.
        </p>
        <p>
          <em>
            Use a metavariable, <code>$FD</code>, on the left-hand side of an
            assignment, and then re-use that metavariable later as if it were
            the file descriptor.
          </em>
        </p>
      </div>
    ),
    pattern: `x = open($FILENAME, 'r', ...)
...
y.write(...)`,
    answers: [
      `$FD = open($FILENAME, 'r', ...)
...
$FD.write(...)`,
    ],
    target: `fout = open("/tmp/example1.txt", 'r', buffering=1024)
print("do stuff in here")
print("more stuff")
fout.write("whoops, I'm not writable!")`,
    followupTitle: "Excellent!",
    followup: (
      <>
        <p>
          Of course, you could have matched this specific example by using the
          variable <code>fout</code> instead of the metavariable{" "}
          <code>$FD</code>.
        </p>
        <p>
          However, if during a refactor, someone changed the name of this
          variable from <code>fout</code> to <code>fd</code> or anything else,
          the pattern would no longer match, although the code would still have
          a bug{" "}
          <span role="img" aria-label=":bug:">
            🐛
          </span>
          .
        </p>
        <p>That is the power of metavariables.</p>
        <span style={{ fontSize: "2em" }} role="img" aria-label=":flex:">
          💪
        </span>
      </>
    ),
  },
  {
    title: "Metavariables vs. Ellipses",
    content: (
      <div>
        <p>
          Metavariables differ from the ellipsis operator because they{" "}
          <strong>must match an expression</strong>, whereas the ellipsis
          operator can match zero or more expressions.
        </p>
        <p>
          As an example, see this pattern that specifies two metavariables. It
          says, "find all calls to <code>open()</code> with two arguments, but I
          don't know what they are."
        </p>
        <p>
          Click <strong>Run</strong> and examine the matches. Line 5 in the test
          code did not match because it has more than two arguments.{" "}
        </p>
        <p>
          <em>
            Try to match all calls to <code>open</code> with at least two
            arguments. How might you say "I don't care about what is after the
            first two arguments?"
          </em>
        </p>
      </div>
    ),
    pattern: `open($FILENAME, $RW)`,
    answers: [`open($FILENAME, $RW, ...)`],
    target: `open("/tmp/example1.txt", 'r')
open("/tmp/example2.txt", 'w')
open(get_example3(), 'w+b')
# Try to match here.
open("/tmp/example4.txt", 'a', buffering=1024)`,
    followupTitle: "Nice work!",
    followup: (
      <>
        <p>
          Using a metavariable tells Semgrep, "something is here, but I don't
          know what it is." Using an ellipsis tells Semgrep, "I don't care what
          is between here and there."
        </p>
        <p>
          How could you change this to match only on calls that specify{" "}
          <code>buffering</code>?
        </p>
        <span style={{ fontSize: "2em" }} role="img" aria-label=":thinking:">
          🤔
        </span>
        <p>We will get to keyword arguments next!</p>
      </>
    ),
  },
  {
    title: "BONUS: match keyword arguments in any order",
    content: (
      <div>
        <p>
          Semgrep understands the <strong>semantics</strong> of the language it
          is scanning. As an example, Semgrep matches keyword arguments{" "}
          <em>in any order</em> in Python.
        </p>
        <p>
          <em>
            Write a Semgrep pattern to match on calls to{" "}
            <code>requests.get</code> which specify any string as a url
            (positional argument), any timeout value, and that verify is true.
            Use <code>$SEC</code> as your time metavariable.
          </em>
        </p>
      </div>
    ),
    pattern: `requests.get(...)`,
    answers: [
      `requests.get("...", timeout=$SEC, verify=True)`,
      `requests.get("...", timeout=$SEC, verify=True,)`,
      `requests.get("...", verify=True, timeout=$SEC)`,
      `requests.get("...", verify=True, timeout=$SEC, )`,
    ],
    target: `import requests

# match these
requests.get("semgrep.dev", timeout=30, verify=True)
requests.get("semgrep.dev", verify=True, timeout=10)

# but do not match these
requests.get("semgrep.dev", timeout=10)
requests.get("semgrep.dev", verify=True)
requests.get("semgrep.dev", verify=False, timeout=10)
requests.get("semgrep.dev", secure=True, timeout=10)
`,
    followupTitle: "You are a Metavariables Champ!",
    followup: (
      <>
        <p>
          Now that you know all about ellipses and metavariables, you can write
          some powerful patterns to find bugs and vulnerabilities.
        </p>
        <span style={{ fontSize: "2em" }} role="img" aria-label=":thinking:">
          🧙🏾🧙‍♀️🧙🏽‍♀️🧙‍♂️
        </span>
        <p>
          However, often one pattern is not enough to specify exactly the
          situations you do and do not want Semgrep to find.
        </p>
        <p>
          See how to combine patterns together in the{" "}
          <Link to="/learn">next tutorial</Link>.
        </p>
      </>
    ),
  },
];
